import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { S } from '../styles';
import { API, authHeaders } from '../utils/helpers';
import { getTheme } from '../theme';
import TicketDetail from './TicketDetail';
import SmsConversation from './SmsConversation';
import { ContactEdit, ConfirmTextLine, NoPhoneHint } from './BookingContact';
import { toastNotified } from '../utils/toast';
import CustomerMatch from './CustomerMatch';

// ── Fittings calendar, under Bookings (3 Oct) ─────────────────────────────────
//
// A day with a column per fitter: their hours (outside them is hatched), the
// fits booked to them, and time blocked off. Click a fit for its details and
// actions; drag it to another time or fitter to move it, which asks first and
// texts the customer. A week view lists the fits day by day.
//
// Everything comes from /book/fit/staff/*. Times are the shop's (the server
// sends its time zone) whatever the computer's clock says.

const HOUR_PX = 64;
const MIN_PX = HOUR_PX / 60;
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// ── Dates and the shop's clock ──
const addDays = (date, n) => {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
};
const weekday = (date) => { const [y, m, d] = date.split('-').map(Number); return new Date(Date.UTC(y, m - 1, d)).getUTCDay(); };
const dayLabel = (date, opts) => { const [y, m, d] = date.split('-').map(Number); return new Date(y, m - 1, d).toLocaleDateString('en-US', opts); };
const toMin = (hhmm) => { const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm || ''); return m ? Number(m[1]) * 60 + Number(m[2]) : null; };
const clock = (min) => { const h = Math.floor(min / 60) % 24, m = min % 60; return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`; };
const hourLabel = (h) => `${((h + 11) % 12) + 1} ${h < 12 ? 'AM' : 'PM'}`;
// Minutes after midnight, shop time, of an instant.
function minutesIn(iso, tz) {
  const p = {};
  for (const x of new Intl.DateTimeFormat('en-US', { timeZone: tz, hourCycle: 'h23', hour: '2-digit', minute: '2-digit' }).formatToParts(new Date(iso))) p[x.type] = x.value;
  return (Number(p.hour) % 24) * 60 + Number(p.minute);
}
const dateIn = (iso, tz) => new Date(iso).toLocaleDateString('en-CA', { timeZone: tz });
// '2026-10-07' + 600 minutes, shop time -> ISO instant.
function zonedIso(date, minutes, tz) {
  const [y, m, d] = date.split('-').map(Number);
  const guess = Date.UTC(y, m - 1, d, 0, minutes);
  const offset = (ms) => {
    const p = {};
    for (const x of new Intl.DateTimeFormat('en-US', { timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }).formatToParts(new Date(ms))) p[x.type] = x.value;
    return Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour % 24, +p.minute, +p.second) - Math.floor(ms / 1000) * 1000;
  };
  let ms = guess - offset(guess);
  ms = guess - offset(ms);
  return new Date(ms).toISOString();
}
const fmtPhone = (p) => { const d = String(p || '').replace(/\D/g, '').slice(-10); return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : (p || ''); };
// "Height: 5 ft 7 in", but "How do you ride? Road" -- no colon after a question mark.
const qa = (label) => (/[?:]$/.test(label.trim()) ? label.trim() : `${label.trim()}:`);
const firstName = (n) => (n || '').trim().split(/\s+/)[0] || 'the customer';
const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const lengthText = (m) => (m % 60 === 0 ? `${m / 60} hour${m === 60 ? '' : 's'}` : m > 60 ? `${Math.floor(m / 60)} hr ${m % 60} min` : `${m} minutes`);

function payText(f) {
  if (!f.paid) return 'pay at fit';
  if (f.paymentStatus === 'authorized') return `${money(f.paid).replace('.00', '')} held`;
  if (f.paymentStatus === 'captured') return `${money(f.paid).replace('.00', '')} paid`;
  if (['refunded', 'voided'].includes(f.paymentStatus)) return 'refunded';
  return 'pay at fit';
}

export default function FittingsCalendar({ theme = 'dark', currentUser = null, focus = null }) {
  // The dark theme's textSub is too faint on the calendar's cards; the
  // journal and Today board use the same lighter grey (effortUi.colors).
  const T = { ...getTheme(theme), ...(theme === 'dark' ? { textSub: '#8a98b5' } : {}) };
  const isDark = theme === 'dark';
  const [view, setView] = useState('day');
  const [date, setDate] = useState(focus && focus.date ? focus.date : null);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(focus ? focus.id : null);
  const [modal, setModal] = useState(null);        // { kind: 'book' | 'block' | 'move' | 'confirm' | 'ticket' | 'text', ... }
  const [drag, setDrag] = useState(null);          // { fit, slots, over: { fitterUserId, time } }
  const dragRef = useRef(null);

  const range = useMemo(() => {
    if (!date) return null;
    if (view === 'day') return { from: date, to: date };
    const monday = addDays(date, -((weekday(date) + 6) % 7));
    return { from: monday, to: addDays(monday, 6) };
  }, [date, view]);

  const load = useCallback(async () => {
    try {
      const q = range ? `?from=${range.from}&to=${range.to}` : '';
      const r = await fetch(`${API}/book/fit/staff/calendar${q}`, { credentials: 'include', headers: authHeaders() });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Could not load fittings.');
      setData(d); setError('');
      if (!date) setDate(d.today);
    } catch (err) { setError(err.message); }
  }, [range, date]);
  useEffect(() => { load(); }, [load]);

  const api = async (method, url, body) => {
    const r = await fetch(`${API}${url}`, {
      method, credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: body ? JSON.stringify(body) : undefined,
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.error || 'That did not work.');
    return d;
  };

  if (!data || !date) {
    return <div style={{ color: T.textSub, padding: 40 }} role={error ? 'alert' : undefined}>{error || 'Loading fittings…'}</div>;
  }

  const tz = data.tz;
  const fitters = data.fitters;
  const nameOf = (id) => (fitters.find(f => String(f.userId) === String(id)) || {}).name || 'Unassigned';
  const selected = data.fits.find(f => f.id === selectedId) || null;
  const pkgOf = (f) => data.packages.find(p => String(p.itemID) === String(f.itemId));
  const fitMinutes = (f) => Math.round((new Date(f.endsAt) - new Date(f.startsAt)) / 60000);

  // The day's span: the earliest start to the latest end of anyone's hours,
  // and never less than 9 to 6.
  const dow = weekday(date);
  let dayStart = 9 * 60, dayEnd = 18 * 60;
  for (const f of fitters) {
    const h = (f.hours || [])[dow];
    if (h) { dayStart = Math.min(dayStart, toMin(h.start)); dayEnd = Math.max(dayEnd, toMin(h.end)); }
  }
  for (const f of data.fits) if (dateIn(f.startsAt, tz) === date) { dayStart = Math.min(dayStart, minutesIn(f.startsAt, tz)); dayEnd = Math.max(dayEnd, minutesIn(f.endsAt, tz)); }
  dayStart = Math.floor(dayStart / 60) * 60; dayEnd = Math.ceil(dayEnd / 60) * 60;
  const hours = []; for (let h = dayStart / 60; h < dayEnd / 60; h++) hours.push(h);
  const heightPx = (dayEnd - dayStart) * MIN_PX;
  const shopOpen = (data.openDays || []).includes(dow);

  const hatch = isDark
    ? 'repeating-linear-gradient(135deg, rgba(255,255,255,0.03) 0 8px, rgba(255,255,255,0.07) 8px 16px)'
    : 'repeating-linear-gradient(135deg, rgba(0,0,0,0.025) 0 8px, rgba(0,0,0,0.06) 8px 16px)';

  // ── Dragging ──
  const startDrag = async (e, f) => {
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', String(f.id)); } catch (_) {}
    const state = { fit: f, slots: null, over: null };
    dragRef.current = state; setDrag(state);
    try {
      const d = await api('GET', `/book/fit/staff/slots?date=${date}&exclude=${f.id}`);
      if (dragRef.current && dragRef.current.fit.id === f.id) { dragRef.current = { ...dragRef.current, slots: d.slots }; setDrag(dragRef.current); }
    } catch (_) {}
  };
  const overColumn = (e, fitterUserId) => {
    if (!dragRef.current) return;
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const step = data.rules.startEvery || 30;
    const raw = dayStart + (e.clientY - rect.top) / MIN_PX - 10;
    const time = Math.max(dayStart, Math.round(raw / step) * step);
    const cur = dragRef.current.over;
    if (!cur || cur.time !== time || String(cur.fitterUserId) !== String(fitterUserId)) {
      dragRef.current = { ...dragRef.current, over: { fitterUserId, time } };
      setDrag(dragRef.current);
    }
  };
  const slotFor = (time, fitterUserId) => {
    const s = drag && drag.slots && drag.slots.find(x => x.time === time);
    return s && s.free.some(id => String(id) === String(fitterUserId)) ? s : null;
  };
  const endDrag = () => { dragRef.current = null; setDrag(null); };
  const dropOn = (e, fitterUserId) => {
    e.preventDefault();
    const d = dragRef.current;
    endDrag();
    if (!d || !d.over) return;
    const slot = d.slots && d.slots.find(x => x.time === d.over.time && x.free.some(id => String(id) === String(fitterUserId)));
    if (!slot) return;
    if (slot.startsAt === d.fit.startsAt && String(fitterUserId) === String(d.fit.fitterUserId)) return;
    setModal({ kind: 'confirm', fit: d.fit, slot, fitterUserId });
  };

  // ── Actions ──
  const move = async ({ fit, startsAt, fitterUserId, notify }) => {
    const d = await api('PUT', `/book/fit/staff/${fit.id}/move`, { startsAt, fitterUserId, notify });
    if (notify !== false) toastNotified(fit.customerName, d.notified, 'the new time');
    setModal(null);
    await load();
  };
  const cancelFit = async (f) => {
    let msg = `Cancel ${f.customerName}'s ${f.itemName}? The Lightspeed ticket moves to your cancellation status (it is not deleted).`;
    if (f.paymentStatus === 'captured' && f.paid) msg += ` Their ${money(f.paid)} deposit will be refunded.`;
    if (f.paymentStatus === 'authorized' && f.paid) msg += ` The ${money(f.paid)} hold on their card will be released.`;
    msg += '\n\nThey will get a text and an email.';
    if (!window.confirm(msg)) return;
    try {
      const d = await api('PUT', `/book/staff/${f.id}/status`, { status: 'canceled' });
      toastNotified(f.customerName, d.notified, 'the cancellation');
      setSelectedId(null);
      await load();
    } catch (err) { setError(err.message); }
  };
  const removeBlock = async (b) => {
    if (!window.confirm(`Remove this blocked time${b.note ? ` (${b.note})` : ''}?`)) return;
    try { await api('DELETE', `/book/fit/staff/blocks/${b.id}`); await load(); } catch (err) { setError(err.message); }
  };

  const btn = (primary, extra) => ({ ...S.btn(primary), fontSize: 13, padding: '7px 14px', ...(primary ? { background: '#e67e22', color: '#fff' } : { color: T.text, borderColor: T.inputBorder }), ...extra });
  const segBtn = (on) => ({ fontSize: 13, padding: '6px 14px', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontWeight: on ? 700 : 400,
    background: on ? (isDark ? '#e67e22' : '#1a2336') : 'transparent', color: on ? '#fff' : T.textSub });

  // ── Pieces ──
  const fitCard = (f, style, compact, short) => {
    const sel = f.id === selectedId;
    const edge = sel ? '#e67e22' : T.cardBorder;
    return (
      <div key={f.id} draggable={view === 'day'} onDragStart={e => startDrag(e, f)} onDragEnd={endDrag}
        role="button" tabIndex={0} onClick={() => setSelectedId(sel ? null : f.id)}
        onKeyDown={e => { if (e.key === 'Enter') setSelectedId(sel ? null : f.id); }}
        title="Click for details. Drag to move."
        style={{ background: isDark ? '#22314f' : '#fff', borderTop: `1px solid ${edge}`, borderRight: `1px solid ${edge}`, borderBottom: `1px solid ${edge}`, borderLeft: '4px solid #e67e22',
          borderRadius: 8, padding: compact ? '6px 8px' : '6px 9px', overflow: 'hidden', cursor: 'grab', boxSizing: 'border-box',
          boxShadow: sel ? '0 0 0 2px #e67e2255' : (isDark ? 'none' : '0 1px 3px rgba(0,0,0,0.08)'),
          opacity: drag && drag.fit.id === f.id ? 0.45 : 1, ...style }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: T.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.customerName}</div>
        <div style={{ fontSize: 12, color: T.textSub, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {compact ? `${clock(minutesIn(f.startsAt, tz))} · ${f.itemName}` : short ? `${f.itemName}${f.bike ? ` · ${f.bike}` : ''}` : f.itemName}
        </div>
        {!compact && !short && f.bike && <div style={{ fontSize: 12, color: T.textSub, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.bike}</div>}
        <div style={{ fontSize: 11, color: T.textSub, marginTop: 2, whiteSpace: 'nowrap' }}>
          {f.workorderId ? `SVC-${f.workorderId} · ` : ''}{payText(f)}{compact ? ` · ${nameOf(f.fitterUserId)}` : ''}
        </div>
      </div>
    );
  };

  const dayView = (
    <div style={{ display: 'flex', border: `1px solid ${T.cardBorder}`, borderRadius: 12, background: T.card, overflow: 'hidden', minWidth: 0 }}>
      <div style={{ width: 58, flexShrink: 0, borderRight: `1px solid ${T.cardBorder}` }}>
        <div style={{ height: 44, borderBottom: `1px solid ${T.cardBorder}` }} />
        <div style={{ position: 'relative', height: heightPx }}>
          {hours.map(h => (
            <div key={h} style={{ position: 'absolute', top: (h * 60 - dayStart) * MIN_PX - 7, right: 8, fontSize: 11, color: T.textSub }}>{hourLabel(h)}</div>
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', flex: 1, minWidth: 0, overflowX: 'auto' }}>
        {fitters.length === 0 && <div style={{ padding: 30, color: T.textSub, fontSize: 14 }}>No fitters yet. Add them in Settings, Online Booking, Fittings.</div>}
        {fitters.map(fitter => {
          const h = (fitter.hours || [])[dow];
          const s = h ? toMin(h.start) : null, e2 = h ? toMin(h.end) : null;
          const mine = data.fits.filter(f => String(f.fitterUserId) === String(fitter.userId) && dateIn(f.startsAt, tz) === date);
          const blocks = data.blocks.filter(b => (b.fitterUserId == null || String(b.fitterUserId) === String(fitter.userId)));
          const over = drag && drag.over && String(drag.over.fitterUserId) === String(fitter.userId) ? drag.over : null;
          const overSlot = over ? slotFor(over.time, fitter.userId) : null;
          const len = drag ? fitMinutes(drag.fit) : 0;
          return (
            <div key={fitter.userId} style={{ flex: '1 0 200px', minWidth: 200, borderRight: `1px solid ${T.cardBorder}` }}>
              <div style={{ height: 44, borderBottom: `1px solid ${T.cardBorder}`, padding: '0 12px', display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{fitter.name}</span>
                <span style={{ fontSize: 12, color: T.textSub }}>· {mine.length} fit{mine.length === 1 ? '' : 's'}</span>
                {fitter.bookable === false && <span style={{ fontSize: 11, color: T.textSub, marginLeft: 'auto' }}>not bookable online</span>}
              </div>
              <div onDragOver={e => overColumn(e, fitter.userId)} onDrop={e => dropOn(e, fitter.userId)}
                style={{ position: 'relative', height: heightPx, background: hatch }}>
                {/* Working hours: plain. Outside them: hatched. */}
                {h && shopOpen && (
                  <div style={{ position: 'absolute', left: 0, right: 0, top: (s - dayStart) * MIN_PX, height: (e2 - s) * MIN_PX, background: T.card }} />
                )}
                {(!h || !shopOpen) && (
                  <div style={{ position: 'absolute', top: 12, left: 0, right: 0, textAlign: 'center', fontSize: 12, color: T.textSub }}>{shopOpen ? 'Not working' : 'Shop closed'}</div>
                )}
                {hours.map(hr => (
                  <div key={hr} style={{ position: 'absolute', left: 0, right: 0, top: (hr * 60 - dayStart) * MIN_PX, borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}` }} />
                ))}
                {/* While dragging: the times that clash, greyed. */}
                {drag && drag.slots && drag.slots.filter(x => !x.free.some(id => String(id) === String(fitter.userId))).map(x => (
                  <div key={x.time} style={{ position: 'absolute', left: 0, right: 0, top: (x.time - dayStart) * MIN_PX, height: (data.rules.startEvery || 30) * MIN_PX, background: isDark ? 'rgba(0,0,0,0.28)' : 'rgba(0,0,0,0.07)' }} />
                ))}
                {blocks.map(b => {
                  if (dateIn(b.startsAt, tz) !== date && dateIn(b.endsAt, tz) !== date) return null;
                  const bs = Math.max(dayStart, dateIn(b.startsAt, tz) === date ? minutesIn(b.startsAt, tz) : dayStart);
                  const be = Math.min(dayEnd, dateIn(b.endsAt, tz) === date ? minutesIn(b.endsAt, tz) : dayEnd);
                  if (be <= bs) return null;
                  return (
                    <div key={b.id} style={{ position: 'absolute', left: 6, right: 6, top: (bs - dayStart) * MIN_PX + 1, height: (be - bs) * MIN_PX - 2,
                      background: isDark ? '#2a3550' : '#e9e9e2', border: `1px dashed ${T.inputBorder}`, borderRadius: 8, padding: '5px 8px', boxSizing: 'border-box', overflow: 'hidden' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 12, color: T.textSub, fontWeight: 600 }}>Blocked{b.note ? ` · ${b.note}` : ''}{b.fitterUserId == null ? ' (everyone)' : ''}</span>
                        <button type="button" onClick={() => removeBlock(b)} title="Remove"
                          style={{ marginLeft: 'auto', background: 'transparent', border: 'none', color: T.textSub, cursor: 'pointer', fontSize: 14, padding: 0 }}>×</button>
                      </div>
                    </div>
                  );
                })}
                {mine.map(f => {
                  const fs = minutesIn(f.startsAt, tz);
                  // Under 90 minutes there is room for three lines, not four.
                  return fitCard(f, { position: 'absolute', left: 6, right: 6, top: (fs - dayStart) * MIN_PX + 1, height: fitMinutes(f) * MIN_PX - 2 }, false, fitMinutes(f) < 90);
                })}
                {over && (
                  <div style={{ position: 'absolute', left: 6, right: 6, top: (over.time - dayStart) * MIN_PX + 1, height: len * MIN_PX - 2, pointerEvents: 'none',
                    border: `2px dashed ${overSlot ? '#27ae60' : '#c0392b'}`, borderRadius: 8, background: overSlot ? '#27ae6018' : '#c0392b12', padding: '6px 9px', boxSizing: 'border-box' }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: overSlot ? '#1e8449' : '#c0392b' }}>
                      {!drag.slots ? 'Checking…' : overSlot ? `Move here: ${clock(over.time)} to ${clock(over.time + len)}` : 'Not free then'}
                    </div>
                    <div style={{ fontSize: 11, color: T.textSub }}>{fitter.name} · {dayLabel(date, { weekday: 'short', month: 'short', day: 'numeric' })}</div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const weekView = range && (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(130px, 1fr))', gap: 8, overflowX: 'auto' }}>
      {Array.from({ length: 7 }, (_, i) => addDays(range.from, i)).map(d => {
        const list = data.fits.filter(f => dateIn(f.startsAt, tz) === d).sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt));
        const isToday = d === data.today;
        return (
          <div key={d} style={{ background: T.card, border: `1px solid ${isToday ? '#e67e22' : T.cardBorder}`, borderRadius: 10, padding: 8, minHeight: 160 }}>
            <button type="button" onClick={() => { setDate(d); setView('day'); }}
              style={{ background: 'transparent', border: 'none', padding: '2px 2px 8px', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: isToday ? '#e67e22' : T.textSub, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{dayLabel(d, { weekday: 'short' })}</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: T.text }}>{dayLabel(d, { month: 'short', day: 'numeric' })}</div>
            </button>
            <div style={{ display: 'grid', gap: 6 }}>
              {list.length === 0 && <div style={{ fontSize: 12, color: T.textSub }}>No fits</div>}
              {list.map(f => fitCard(f, {}, true))}
            </div>
          </div>
        );
      })}
    </div>
  );

  const detail = selected && (() => {
    const f = selected;
    const fs = minutesIn(f.startsAt, tz), mins = fitMinutes(f);
    const due = Math.max(0, f.itemPrice - (['captured', 'authorized'].includes(f.paymentStatus) ? f.paid : 0));
    const row = (label, value) => value ? (
      <div style={{ display: 'grid', gridTemplateColumns: '78px 1fr', gap: 8, fontSize: 13, padding: '3px 0' }}>
        <span style={{ color: T.textSub }}>{label}</span><span style={{ color: T.text }}>{value}</span>
      </div>
    ) : null;
    return (
      <div style={{ width: 320, flexShrink: 0, background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: 16, alignSelf: 'flex-start', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {f.workorderId && <span style={{ fontSize: 12, fontWeight: 700, color: '#1A2535', background: '#E67E22', borderRadius: 5, padding: '2px 7px' }}>SVC-{f.workorderId}</span>}
          <span style={{ fontSize: 12, fontWeight: 600, padding: '2px 9px', borderRadius: 10, background: '#27ae6022', color: isDark ? '#2ecc71' : '#1e8449' }}>{f.status === 'arrived' ? 'Arrived' : 'Booked'}</span>
          {f.source === 'staff' && <span style={{ fontSize: 11, color: T.textSub }}>phone booking</span>}
          <button type="button" onClick={() => setSelectedId(null)} title="Close" style={{ marginLeft: 'auto', background: 'transparent', border: 'none', color: T.textSub, cursor: 'pointer', fontSize: 16 }}>×</button>
        </div>
        <div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: T.text }}>{f.customerName}</div>
            {f.status !== 'canceled' && <button type="button" onClick={() => setModal({ kind: 'contact', fit: f })} style={{ background: 'transparent', border: 'none', padding: 0, color: '#e67e22', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>Edit</button>}
          </div>
          {f.bike && <div style={{ fontSize: 13, color: T.textSub }}>{f.bike}</div>}
          <ConfirmTextLine T={T} confirmText={f.confirmText} phone={f.customerPhone} />
        </div>
        <div>
          {row('Service', `${f.itemName} · ${lengthText(mins)}`)}
          {row('When', `${dayLabel(dateIn(f.startsAt, tz), { weekday: 'short', month: 'short', day: 'numeric' })} · ${clock(fs)} to ${clock(fs + mins)}`)}
          {row('Fitter', nameOf(f.fitterUserId))}
          {row('Payment', f.paid && ['captured', 'authorized'].includes(f.paymentStatus)
            ? `Deposit ${money(f.paid)} ${f.paymentStatus === 'captured' ? 'paid' : 'held'} · ${money(due)} due at fit`
            : `${money(f.itemPrice)} due at fit`)}
          {row('Phone', f.customerPhone && fmtPhone(f.customerPhone))}
          {row('Email', f.customerEmail)}
        </div>
        {(f.answers.length > 0 || f.notes) && (
          <div style={{ background: isDark ? '#1e2a40' : '#f4f4ef', borderRadius: 8, padding: '10px 12px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.textSub, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 4 }}>From the booking</div>
            {f.answers.map(a => <div key={a.id} style={{ fontSize: 13, color: T.text }}><span style={{ color: T.textSub }}>{qa(a.label)}</span> {a.answer}</div>)}
            {f.notes && <div style={{ fontSize: 13, color: T.text, marginTop: 4, fontStyle: 'italic' }}>"{f.notes}"</div>}
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <button type="button" disabled={!f.workorderId} style={btn(true, { opacity: f.workorderId ? 1 : 0.5 })} onClick={() => setModal({ kind: 'ticket', fit: f })}>Open ticket</button>
          <button type="button" disabled={!f.customerPhone} style={btn(false, { opacity: f.customerPhone ? 1 : 0.5 })} onClick={() => setModal({ kind: 'text', fit: f })}>Text customer</button>
          <button type="button" style={btn(false)} onClick={() => setModal({ kind: 'move', fit: f })}>Reschedule</button>
          <button type="button" style={btn(false, { color: '#e74c3c', borderColor: '#e74c3c44' })} onClick={() => cancelFit(f)}>Cancel</button>
        </div>
        <div style={{ fontSize: 12, color: T.textSub }}>Tip: drag the fit on the calendar to move it.</div>
      </div>
    );
  })();

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        <button type="button" style={btn(false, { padding: '7px 12px' })} onClick={() => setDate(addDays(date, view === 'day' ? -1 : -7))} aria-label="Earlier">‹</button>
        <div style={{ fontSize: 17, fontWeight: 700, color: T.text, minWidth: 220, textAlign: 'center' }}>
          {view === 'day' ? dayLabel(date, { weekday: 'long', month: 'long', day: 'numeric' })
            : `${dayLabel(range.from, { month: 'short', day: 'numeric' })} to ${dayLabel(range.to, { month: 'short', day: 'numeric' })}`}
        </div>
        <button type="button" style={btn(false, { padding: '7px 12px' })} onClick={() => setDate(addDays(date, view === 'day' ? 1 : 7))} aria-label="Later">›</button>
        <button type="button" style={btn(false)} onClick={() => setDate(data.today)}>Today</button>
        <span style={{ display: 'inline-flex', border: `1px solid ${T.inputBorder}`, borderRadius: 8, overflow: 'hidden' }}>
          <button type="button" style={segBtn(view === 'day')} onClick={() => setView('day')}>Day</button>
          <button type="button" style={segBtn(view === 'week')} onClick={() => setView('week')}>Week</button>
        </span>
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button type="button" style={btn(true)} disabled={!data.packages.length} onClick={() => setModal({ kind: 'book' })}>+ Book a fit</button>
          <button type="button" style={btn(false)} onClick={() => setModal({ kind: 'block' })}>Block time</button>
        </span>
      </div>
      {error && <div role="alert" style={{ fontSize: 13, color: '#e74c3c', marginBottom: 10 }}>{error}</div>}
      {!data.packages.length && (
        <div style={{ fontSize: 13, color: T.textSub, marginBottom: 12 }}>No package is booked by appointment yet. Mark one as an Appointment in Settings, Online Booking, Fittings.</div>
      )}
      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>{view === 'day' ? dayView : weekView}</div>
        {detail}
      </div>

      {modal && modal.kind === 'confirm' && (
        <ConfirmMove T={T} isDark={isDark} modal={modal} nameOf={nameOf} date={date} tz={tz}
          onClose={() => setModal(null)} onMove={(notify) => move({ fit: modal.fit, startsAt: modal.slot.startsAt, fitterUserId: modal.fitterUserId, notify })} />
      )}
      {modal && modal.kind === 'move' && (
        <PickTime T={T} isDark={isDark} title={`Reschedule ${modal.fit.customerName}`} api={api} data={data}
          initialDate={dateIn(modal.fit.startsAt, tz)} itemID={modal.fit.itemId} exclude={modal.fit.id} fitters={fitters}
          onClose={() => setModal(null)}
          onPick={(slot, fitterUserId) => setModal({ kind: 'confirm', fit: modal.fit, slot, fitterUserId })} />
      )}
      {modal && modal.kind === 'book' && (
        <BookFit T={T} isDark={isDark} api={api} data={data} date={date} fitters={fitters}
          onClose={() => setModal(null)} onBooked={async (fit) => { setModal(null); await load(); setSelectedId(fit.id); }} />
      )}
      {modal && modal.kind === 'block' && (
        <BlockTime T={T} isDark={isDark} api={api} date={date} fitters={fitters} tz={tz}
          onClose={() => setModal(null)} onDone={async () => { setModal(null); await load(); }} />
      )}
      {modal && modal.kind === 'contact' && <ContactEdit T={T} booking={modal.fit} onClose={() => setModal(null)} onSaved={() => load()} />}
      {modal && modal.kind === 'ticket' && (
        <TicketDetail ticketId={modal.fit.workorderId} onClose={() => { setModal(null); load(); }} theme={theme} currentUser={currentUser} />
      )}
      {modal && modal.kind === 'text' && (
        <Modal T={T} title={`Text ${modal.fit.customerName}`} onClose={() => setModal(null)} width={520}>
          <div style={{ height: 460, display: 'flex', flexDirection: 'column' }}>
            <SmsConversation phone={modal.fit.customerPhone} ticketId={modal.fit.workorderId} customerId={null}
              customerName={modal.fit.customerName} bikeLabel={modal.fit.bike || ''} balance={0} theme={theme} markReadOnOpen={true} />
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Dialogs ──

function Modal({ T, title, children, onClose, width = 460 }) {
  return (
    <div role="dialog" aria-modal="true" onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(5,10,20,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ width: '100%', maxWidth: width, maxHeight: '90vh', overflowY: 'auto', background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: 20, boxShadow: '0 20px 60px rgba(0,0,0,0.35)' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.text }}>{title}</div>
          <button type="button" onClick={onClose} title="Close" style={{ marginLeft: 'auto', background: 'transparent', border: 'none', color: T.textSub, cursor: 'pointer', fontSize: 18 }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ConfirmMove({ T, modal, nameOf, tz, onClose, onMove }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const { fit, slot, fitterUserId } = modal;
  const label = dayLabel(dateIn(slot.startsAt, tz), { weekday: 'short', month: 'short', day: 'numeric' });
  const go = async (notify) => {
    setBusy(true); setError('');
    try { await onMove(notify); } catch (err) { setError(err.message); setBusy(false); }
  };
  const sameFitter = String(fitterUserId) === String(fit.fitterUserId);
  return (
    <Modal T={T} title={`Move ${fit.customerName}?`} onClose={onClose}>
      <div style={{ fontSize: 14, color: T.text, lineHeight: 1.6, marginBottom: 14 }}>
        To <strong>{label}, {slot.label}</strong>{sameFitter ? '' : <> with <strong>{nameOf(fitterUserId)}</strong></>}.
        {' '}The ticket's due date{sameFitter ? '' : ' and tech'} change{sameFitter ? 's' : ''} in Lightspeed.
      </div>
      {error && <div role="alert" style={{ fontSize: 13, color: '#e74c3c', marginBottom: 10 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button type="button" disabled={busy} onClick={() => go(true)} style={{ ...S.btn(true), background: '#e67e22', color: '#fff', opacity: busy ? 0.6 : 1 }}>
          {busy ? 'Moving…' : `Move and text ${firstName(fit.customerName)}`}
        </button>
        <button type="button" disabled={busy} onClick={() => go(false)} style={{ ...S.btn(false), color: T.text, borderColor: T.inputBorder }}>Move without a text</button>
        <button type="button" disabled={busy} onClick={onClose} style={{ ...S.btn(false), color: T.textSub, borderColor: 'transparent' }}>Cancel</button>
      </div>
    </Modal>
  );
}

// Day, fitter and a free time, from /book/fit/staff/slots.
function TimeChooser({ T, api, itemID, exclude, fitters, date, setDate, fitter, setFitter, value, onChange }) {
  const [slots, setSlots] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!date || (!itemID && !exclude)) { setSlots(null); return; }
    let live = true;
    setSlots(null); setError('');
    const q = new URLSearchParams({ date });
    if (itemID) q.set('itemID', itemID);
    if (exclude) q.set('exclude', exclude);
    if (fitter && fitter !== 'any') q.set('fitter', fitter);
    api('GET', `/book/fit/staff/slots?${q}`).then(d => { if (live) setSlots(d.slots); }).catch(err => { if (live) setError(err.message); });
    return () => { live = false; };
  }, [date, itemID, exclude, fitter]); // api is stable enough: it only closes over constants
  const input = { background: T.input, border: `1px solid ${T.inputBorder}`, borderRadius: 6, padding: '7px 10px', color: T.text, fontSize: 13, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' };
  const nameOf = (id) => (fitters.find(f => String(f.userId) === String(id)) || {}).name || '';
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
        <input type="date" value={date || ''} onChange={e => { setDate(e.target.value); onChange(null); }} style={input} />
        <select value={fitter} onChange={e => { setFitter(e.target.value); onChange(null); }} style={input}>
          <option value="any">Any fitter</option>
          {fitters.map(f => <option key={f.userId} value={f.userId}>{f.name}</option>)}
        </select>
      </div>
      {error && <div style={{ fontSize: 13, color: '#e74c3c' }}>{error}</div>}
      {!slots && !error && date && <div style={{ fontSize: 13, color: T.textSub }}>Loading times…</div>}
      {slots && slots.length === 0 && <div style={{ fontSize: 13, color: T.textSub }}>Nobody is working then, or the shop is closed.</div>}
      {slots && slots.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 6 }}>
          {slots.map(s => {
            const free = s.free.length > 0, sel = value && value.startsAt === s.startsAt;
            return (
              <button key={s.startsAt} type="button" disabled={!free} title={free ? s.free.map(nameOf).join(', ') : 'Not free'}
                onClick={() => onChange(s)}
                style={{ padding: '8px 0', borderRadius: 7, fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
                  border: `1px solid ${sel ? '#e67e22' : T.inputBorder}`, background: sel ? '#e67e22' : 'transparent',
                  color: sel ? '#fff' : free ? T.text : T.textSub, textDecoration: free ? 'none' : 'line-through', cursor: free ? 'pointer' : 'default', opacity: free ? 1 : 0.6 }}>
                {s.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PickTime({ T, api, title, initialDate, itemID, exclude, fitters, onClose, onPick }) {
  const [date, setDate] = useState(initialDate);
  const [fitter, setFitter] = useState('any');
  const [slot, setSlot] = useState(null);
  const chosenFitter = slot ? (fitter !== 'any' ? fitter : slot.free[0]) : null;
  return (
    <Modal T={T} title={title} onClose={onClose}>
      <TimeChooser T={T} api={api} itemID={itemID} exclude={exclude} fitters={fitters} date={date} setDate={setDate}
        fitter={fitter} setFitter={setFitter} value={slot} onChange={setSlot} />
      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <button type="button" disabled={!slot} onClick={() => onPick(slot, chosenFitter)}
          style={{ ...S.btn(true), background: '#e67e22', color: '#fff', opacity: slot ? 1 : 0.5 }}>Continue</button>
        <button type="button" onClick={onClose} style={{ ...S.btn(false), color: T.textSub, borderColor: T.inputBorder }}>Cancel</button>
      </div>
    </Modal>
  );
}

function BookFit({ T, isDark, api, data, date: initialDate, fitters, onClose, onBooked }) {
  const [form, setForm] = useState({ customerName: '', customerPhone: '', customerEmail: '', itemID: data.packages[0] ? String(data.packages[0].itemID) : '', bikeDescription: '', notes: '' });
  const [answers, setAnswers] = useState({});
  const [picked, setPicked] = useState(null);
  const [date, setDate] = useState(initialDate);
  const [fitter, setFitter] = useState('any');
  const [slot, setSlot] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const input = { width: '100%', background: T.input, border: `1px solid ${T.inputBorder}`, borderRadius: 6, padding: '8px 10px', color: T.text, fontSize: 13, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' };
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const submit = async () => {
    setError('');
    if (!form.customerName.trim()) return setError('Customer name is required.');
    if (!slot) return setError('Pick a time.');
    setBusy(true);
    try {
      const d = await api('POST', '/book/fit/staff/create', {
        ...form, customerName: form.customerName.trim(), startsAt: slot.startsAt,
        fitterUserId: fitter !== 'any' ? fitter : 'any', answers,
        ...(picked?.id ? { lsCustomerId: String(picked.id) } : {}),
      });
      onBooked(d.fit);
    } catch (err) { setError(err.message); setBusy(false); }
  };
  return (
    <Modal T={T} title="Book a fit" onClose={onClose} width={640}>
      <div style={{ fontSize: 12, color: T.textSub, marginBottom: 12, lineHeight: 1.5 }}>
        Creates the Lightspeed customer, bike and ticket like an online booking, assigned to the fitter, and texts the customer a confirmation. No payment is taken.
      </div>
      <CustomerMatch T={T} isDark={isDark} input={input} form={form} setForm={setForm} selected={picked} onSelect={setPicked} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
        <select style={input} value={form.itemID} onChange={e => { set('itemID')(e); setSlot(null); }}>
          {data.packages.map(p => <option key={p.itemID} value={p.itemID}>{p.name} · {lengthText(p.minutes)}</option>)}
        </select>
        <input style={input} placeholder="Bike, e.g. Trek Domane SL 6, red" value={form.bikeDescription} onChange={set('bikeDescription')} />
        <input style={input} placeholder="Notes" value={form.notes} onChange={set('notes')} />
        {(data.questions || []).filter(q => q.label).map(q => (
          <input key={q.id} style={input} placeholder={q.label} value={answers[q.id] || ''} onChange={e => setAnswers(a => ({ ...a, [q.id]: e.target.value }))} />
        ))}
      </div>
      <NoPhoneHint phone={form.customerPhone} />
      <TimeChooser T={T} api={api} itemID={form.itemID} fitters={fitters} date={date} setDate={setDate}
        fitter={fitter} setFitter={setFitter} value={slot} onChange={setSlot} />
      {error && <div role="alert" style={{ fontSize: 13, color: '#e74c3c', marginTop: 10 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <button type="button" disabled={busy} onClick={submit} style={{ ...S.btn(true), background: '#e67e22', color: '#fff', opacity: busy ? 0.6 : 1 }}>{busy ? 'Booking…' : 'Book the fit'}</button>
        <button type="button" onClick={onClose} style={{ ...S.btn(false), color: T.textSub, borderColor: T.inputBorder }}>Cancel</button>
      </div>
    </Modal>
  );
}

function BlockTime({ T, api, date: initialDate, fitters, tz, onClose, onDone }) {
  const [who, setWho] = useState(fitters[0] ? String(fitters[0].userId) : 'all');
  const [date, setDate] = useState(initialDate);
  const [allDay, setAllDay] = useState(false);
  const [from, setFrom] = useState('12:00');
  const [to, setTo] = useState('13:00');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const input = { width: '100%', background: T.input, border: `1px solid ${T.inputBorder}`, borderRadius: 6, padding: '8px 10px', color: T.text, fontSize: 13, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' };
  const submit = async () => {
    setError('');
    const s = allDay ? 0 : toMin(from), e = allDay ? 24 * 60 : toMin(to);
    if (!date || s === null || e === null || e <= s) return setError('Pick a day, and an end after the start.');
    setBusy(true);
    try {
      await api('POST', '/book/fit/staff/blocks', {
        fitterUserId: who === 'all' ? null : Number(who),
        startsAt: zonedIso(date, s, tz), endsAt: zonedIso(date, e, tz), note,
      });
      onDone();
    } catch (err) { setError(err.message); setBusy(false); }
  };
  return (
    <Modal T={T} title="Block time" onClose={onClose}>
      <div style={{ fontSize: 12, color: T.textSub, marginBottom: 12, lineHeight: 1.5 }}>
        Nobody can book this time online, and it shows on the calendar. For a day off, a holiday, or the fit station out of action.
      </div>
      <div style={{ display: 'grid', gap: 8 }}>
        <select style={input} value={who} onChange={e => setWho(e.target.value)}>
          {fitters.map(f => <option key={f.userId} value={f.userId}>{f.name}</option>)}
          <option value="all">Everyone (the station)</option>
        </select>
        <input type="date" style={input} value={date} onChange={e => setDate(e.target.value)} />
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: T.text }}>
          <input type="checkbox" checked={allDay} onChange={e => setAllDay(e.target.checked)} style={{ accentColor: '#e67e22' }} /> All day
        </label>
        {!allDay && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 8, alignItems: 'center' }}>
            <input type="time" step="900" style={input} value={from} onChange={e => setFrom(e.target.value)} />
            <span style={{ fontSize: 12, color: T.textSub }}>to</span>
            <input type="time" step="900" style={input} value={to} onChange={e => setTo(e.target.value)} />
          </div>
        )}
        <input style={input} placeholder="Why (optional), e.g. dentist" value={note} maxLength={120} onChange={e => setNote(e.target.value)} />
        <div style={{ fontSize: 12, color: T.textSub }}>{DAY_NAMES[weekday(date || initialDate)]}. Fits already booked then stay booked; move them first if they need to.</div>
      </div>
      {error && <div role="alert" style={{ fontSize: 13, color: '#e74c3c', marginTop: 10 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <button type="button" disabled={busy} onClick={submit} style={{ ...S.btn(true), background: '#e67e22', color: '#fff', opacity: busy ? 0.6 : 1 }}>{busy ? 'Saving…' : 'Block it'}</button>
        <button type="button" onClick={onClose} style={{ ...S.btn(false), color: T.textSub, borderColor: T.inputBorder }}>Cancel</button>
      </div>
    </Modal>
  );
}
